/**
 * The trainer's private note: read above, written here.
 *
 * ── An empty save DELETES, and that is mobile's rule ports one for one ────────────────────────
 * `PUT /customer/{id}/note` upserts and `DELETE /customer/{id}/note` clears. The DELETE is
 * **idempotent**: `CustomerController.DeleteCustomerNote` answers 204 whether or not a row existed,
 * in as many words — "so the FE can issue a clear without first checking existence" — and QA
 * confirmed the 204 twice against a client with no note. So the empty branch below sends it
 * unconditionally and never has to read the note first.
 *
 * Saving an empty field on a client who has no note is nonetheless refused one level up, by the
 * component beside the field: it owns the three-way decision because it owns the draft, and a save
 * that changes nothing should not light up as a pending write. That refusal is a UI choice, not a
 * wire constraint — a DELETE that reached the server would be answered, not rejected. What this
 * hook owns is which endpoint each branch reaches and what it invalidates.
 */

import { useQueryClient } from '@tanstack/react-query';

import {
  useDeleteCustomerIdNote,
  usePutCustomerIdNote,
} from '~/shared/api/generated/endpoints/customer/customer';

import { clientNoteQueryKey } from '../api/clientDetailQueries';

export interface ClientNoteEdit {
  readonly save: (body: string) => void;
  readonly saving: boolean;
  readonly failed: boolean;
}

export function useClientNoteEdit(clientId: number): ClientNoteEdit {
  const queryClient = useQueryClient();
  const put = usePutCustomerIdNote();
  const remove = useDeleteCustomerIdNote();

  // The server re-renders `updatedText` ("zmieniona 7 sierpnia") on every write, so the cache is
  // dropped rather than patched — an optimistic body with a stale date beside it is worse than a
  // hundred milliseconds of the old note.
  const settle = () => {
    void queryClient.invalidateQueries({ queryKey: clientNoteQueryKey(clientId) });
  };

  return {
    save: (body: string) => {
      const trimmed = body.trim();
      if (trimmed.length === 0) {
        remove.mutate({ id: clientId }, { onSettled: settle });
        return;
      }
      put.mutate({ id: clientId, data: { body: trimmed } }, { onSettled: settle });
    },
    saving: put.isPending || remove.isPending,
    failed: put.isError || remove.isError,
  };
}
